import React from "react";
import { ArrowLeft, BookOpen, Download, Settings, Shield, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CHROME_WEB_STORE_URL } from "@/config/extension";

export default function Learn() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold">Learn Center</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Understanding DNS Filtering</h1>
          <p className="text-xl text-muted-foreground mb-6">
            Learn how Fendly's DNS filtering technology works and how to set it up for your family or organization.
          </p>
          <div className="flex justify-center space-x-4">
            <Button asChild size="lg">
              <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" />
                Install Chrome Extension
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/help">Get Help</Link>
            </Button>
          </div>
        </div>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-2">
              <a href="#how-dns-works" className="block text-primary hover:underline">How DNS Filtering Works</a>
              <a href="#installation" className="block text-primary hover:underline">Installation Guide</a>
              <a href="#router-config" className="block text-primary hover:underline">Router Configuration</a>
              <a href="#chrome-extension" className="block text-primary hover:underline">Chrome Extension Setup</a>
              <a href="#best-practices" className="block text-primary hover:underline">Best Practices</a>
            </nav>
          </CardContent>
        </Card>

        {/* How DNS Works Section */}
        <section id="how-dns-works" className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">How DNS Filtering Works</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-lg mb-6">
              DNS (Domain Name System) is like the internet's phone book. When you visit a website, your device asks a DNS server 
              to translate the domain name (like "google.com") into an IP address. Fendly intercepts these requests and blocks 
              access to unwanted content before it even loads.
            </p>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Step-by-Step Process</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-4">
                  <Badge variant="secondary" className="mt-1">1</Badge>
                  <div>
                    <h4 className="font-semibold">DNS Query</h4>
                    <p className="text-muted-foreground">Your device requests the IP address for a domain</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Badge variant="secondary" className="mt-1">2</Badge>
                  <div>
                    <h4 className="font-semibold">Policy Check</h4>
                    <p className="text-muted-foreground">Fendly checks your filtering policies against the domain</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Badge variant="secondary" className="mt-1">3</Badge>
                  <div>
                    <h4 className="font-semibold">Decision</h4>
                    <p className="text-muted-foreground">If blocked, returns a safe IP; if allowed, returns the real IP</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Badge variant="secondary" className="mt-1">4</Badge>
                  <div>
                    <h4 className="font-semibold">Logging</h4>
                    <p className="text-muted-foreground">All queries are logged for monitoring and analytics</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold mb-4">Why DNS Filtering?</h3>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Network-wide protection:</strong> Works on all devices connected to your network</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Real-time blocking:</strong> Blocks content instantly without delays</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Bypass-resistant:</strong> Harder to circumvent than software-based solutions</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span><strong>Performance:</strong> Minimal impact on internet speed</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Installation Section */}
        <section id="installation" className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Download className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">Installation Guide</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-5 w-5" />
                  <span>Chrome Extension</span>
                </CardTitle>
                <CardDescription>Quick setup for individual devices</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">1</Badge>
                    <span>Click "Install Chrome Extension" above</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">2</Badge>
                    <span>Add the extension to Chrome</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">3</Badge>
                    <span>Sign in to your Fendly account</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">4</Badge>
                    <span>Start blocking immediately</span>
                  </li>
                </ol>
                <Button className="w-full mt-4" asChild>
                  <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
                    Install Extension
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="h-5 w-5" />
                  <span>Router Setup</span>
                </CardTitle>
                <CardDescription>Network-wide protection for all devices</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">1</Badge>
                    <span>Deploy Fendly DNS server</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">2</Badge>
                    <span>Configure your router's DNS settings</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">3</Badge>
                    <span>Set Fendly DNS as primary DNS server</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Badge variant="secondary" className="mt-1">4</Badge>
                    <span>All devices automatically protected</span>
                  </li>
                </ol>
                <Button variant="outline" className="w-full mt-4" asChild>
                  <a href="#router-config">Configure Router</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Router Configuration Section */}
        <section id="router-config" className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Settings className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">Router Configuration</h2>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg mb-6">
              Configuring your router to use Fendly's DNS servers provides network-wide protection for all connected devices. 
              This is the most effective way to protect your entire family or organization.
            </p>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Supported Router Brands</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold">Popular Brands</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• Linksys Routers</li>
                      <li>• Netgear Routers</li>
                      <li>• ASUS Routers</li>
                      <li>• TP-Link Routers</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold">Advanced Options</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• OpenWrt Firmware</li>
                      <li>• DD-WRT Firmware</li>
                      <li>• Custom Router Configurations</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold mb-4">General Configuration Steps</h3>
            <ol className="space-y-4 mb-6">
              <li className="flex items-start space-x-4">
                <Badge variant="secondary" className="mt-1">1</Badge>
                <div>
                  <h4 className="font-semibold">Access Router Admin</h4>
                  <p className="text-muted-foreground">Open your browser and navigate to your router's admin interface (usually 192.168.1.1 or 192.168.0.1)</p>
                </div>
              </li>
              <li className="flex items-start space-x-4">
                <Badge variant="secondary" className="mt-1">2</Badge>
                <div>
                  <h4 className="font-semibold">Find DNS Settings</h4>
                  <p className="text-muted-foreground">Look for "Internet Setup", "WAN Settings", or "DNS Configuration" in your router's menu</p>
                </div>
              </li>
              <li className="flex items-start space-x-4">
                <Badge variant="secondary" className="mt-1">3</Badge>
                <div>
                  <h4 className="font-semibold">Configure DNS Servers</h4>
                  <p className="text-muted-foreground">Set Primary DNS to your Fendly DNS server IP and Secondary DNS to 8.8.8.8 (Google) or 1.1.1.1 (Cloudflare)</p>
                </div>
              </li>
              <li className="flex items-start space-x-4">
                <Badge variant="secondary" className="mt-1">4</Badge>
                <div>
                  <h4 className="font-semibold">Save and Reboot</h4>
                  <p className="text-muted-foreground">Save your settings and restart your router to apply the changes</p>
                </div>
              </li>
            </ol>

            <div className="bg-muted p-4 rounded-lg mb-6">
              <h4 className="font-semibold mb-2">💡 Pro Tip</h4>
              <p className="text-sm">
                After configuring your router, test the setup by visiting a known blocked domain. You should see Fendly's block page 
                instead of the original content.
              </p>
            </div>
          </div>
        </section>

        {/* Chrome Extension Section */}
        <section id="chrome-extension" className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">Chrome Extension Setup</h2>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg mb-6">
              The Fendly Chrome Extension provides additional protection at the browser level, complementing your router-based 
              DNS filtering. It's perfect for individual devices or when you're away from your home network.
            </p>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Extension Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <h4 className="font-semibold">Search Result Filtering</h4>
                        <p className="text-sm text-muted-foreground">Blocks unwanted search results before they appear</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <h4 className="font-semibold">Real-time Blocking</h4>
                        <p className="text-sm text-muted-foreground">Instantly blocks access to blocked domains</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <h4 className="font-semibold">Activity Monitoring</h4>
                        <p className="text-sm text-muted-foreground">Tracks browsing activity and blocked attempts</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <h4 className="font-semibold">Policy Synchronization</h4>
                        <p className="text-sm text-muted-foreground">Syncs with your Fendly dashboard policies</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Best Practices Section */}
        <section id="best-practices" className="mb-12">
          <div className="flex items-center space-x-3 mb-6">
            <Users className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">Best Practices</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>For Families</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Set Screen Time Limits</h4>
                    <p className="text-sm text-muted-foreground">Use scheduled blocking to enforce healthy screen time habits</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Age-Appropriate Filtering</h4>
                    <p className="text-sm text-muted-foreground">Adjust filtering levels based on your children's ages</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Open Communication</h4>
                    <p className="text-sm text-muted-foreground">Discuss internet safety with your children regularly</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>For Organizations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Compliance Policies</h4>
                    <p className="text-sm text-muted-foreground">Ensure filtering meets industry compliance requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Employee Training</h4>
                    <p className="text-sm text-muted-foreground">Train staff on acceptable use policies</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold">Productivity Hours</h4>
                    <p className="text-sm text-muted-foreground">Block distracting sites during work hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center py-12 bg-muted rounded-lg">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg text-muted-foreground mb-6">
            Join thousands of families and organizations already protected by Fendly.
          </p>
          <div className="flex justify-center space-x-4">
            <Button size="lg" asChild>
              <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
                Install Chrome Extension
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/auth">Start Free Trial</Link>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
